import React, { useContext, useState, useEffect, useCallback } from "react";
import { useSnackbar } from "notistack";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { Button, Grid, Box, TextField, CircularProgress, Typography, Chip, Avatar } from "@material-ui/core";
import PersonIcon from "@material-ui/icons/Person";
import CloseIcon from "@material-ui/icons/Close";
import CheckIcon from "@material-ui/icons/Check";

import Layout from "../components/Layout/Layout";
import WorklineListItem from "../components/WorklineListItem/WorklineListItem";
import { useFailInspectionMutation, useFetchInspectionLineByIdQuery, usePassInspectionMutation, useUpdateInspectionLineCommentMutation, useGetProjectLine } from "../api";
import { PRIVILEGES, SERVICE_TYPES, SERVICE_SECTIONS } from "../constants";
import { hasPrivilege } from "../utils/privilegeUtils";
import { UserDataContext } from "../app/App";
import LoadingButton from "../components/LoadingButton/LoadingButton";
import ErrorPage from "./ErrorPage";

function InspectionDetails() {
    const [inspectionComment, setInspectionComment] = useState("");
    const [isCommentChanged, setIsCommentChanged] = useState(false);
    const [inspectionPassed, setInspectionPassed] = useState(false);

    const { enqueueSnackbar } = useSnackbar();
    const history = useHistory();
    const location = useLocation();
    const { id } = useParams(); // inspection line id

    const { user } = useContext(UserDataContext);
    const isAdmin = hasPrivilege(user.crmPrivileges, PRIVILEGES.INSPECTION_SUPER);

    // queries
    const {
        data: inspectionLine,
        isLoading: isLoadingInspectionLine,
        isError: isErrorInspectionLine,
        isSuccess: isSuccessInspectionLine,
        refetch: refetchInspectionLine,
    } = useFetchInspectionLineByIdQuery(id);

    // Fetch the project line only if the inspection line has a project line
    const { data: projectLine, isLoading: isLoadingProjectLine, isError: isErrorProjectLine } = useGetProjectLine(inspectionLine?.projectLine?.id, { enabled: !!inspectionLine?.projectLine?.id });

    // effects
    // Set the inspection comment when the inspection line is loaded
    useEffect(() => {
        if (isSuccessInspectionLine && inspectionLine) {
            setInspectionComment(inspectionLine.comment ?? "");
            setInspectionPassed(inspectionLine.passedInspection);
        }
    }, [isSuccessInspectionLine, inspectionLine]);
    // Check if the comment has changed
    useEffect(() => {
        setIsCommentChanged(inspectionComment !== inspectionLine?.comment);
    }, [inspectionComment, inspectionLine, inspectionPassed]);

    // mutations
    const { mutate: mutateUpdateComment, isLoading: isLoadingUpdateComment } = useUpdateInspectionLineCommentMutation();
    const { mutate: mutateFailInspection, isLoading: isFailInspectionLoading } = useFailInspectionMutation();
    const { mutate: mutatePassInspection, isLoading: isPassInspectionLoading } = usePassInspectionMutation();

    // handlers
    const handleChangeComment = (event) => {
        setInspectionComment(event.target.value);
    };

    const handleUpdateInspectionLineComment = async () => {
        if (id && isCommentChanged) {
            mutateUpdateComment(
                {
                    inspectionLineId: id,
                    comment: inspectionComment,
                },
                {
                    onSuccess: () => {
                        enqueueSnackbar("Inspection comment updated successfully!", {
                            variant: "success",
                            anchorOrigin: { vertical: "bottom", horizontal: "center" },
                            autoHideDuration: 3000,
                        });
                        setIsCommentChanged(false);
                    },
                    onError: () => {
                        enqueueSnackbar("Error occurred while updating comment", {
                            variant: "error",
                            anchorOrigin: { vertical: "bottom", horizontal: "center" },
                            autoHideDuration: 3000,
                        });
                    },
                }
            );
        }
    };

    const handleCancelInspectionLineComment = () => {
        // Reset the comment and the change flag
        if (inspectionLine) {
            setInspectionComment(inspectionLine.comment);
            setIsCommentChanged(false);
        }
    };

    const handlePassInspection = () => {
        mutatePassInspection(
            {
                inspectionLineId: inspectionLine.id,
                InspectorId: user.id,
            },
            {
                onSuccess: () => {
                    enqueueSnackbar("Inspection marked as passed successfully", {
                        variant: "success",
                        anchorOrigin: { vertical: "bottom", horizontal: "center" },
                        autoHideDuration: 3000,
                    });
                    setInspectionPassed(true);
                    refetchInspectionLine();
                },
                onError: () => {
                    enqueueSnackbar("Error occurred while passing the inspection", {
                        variant: "error",
                        anchorOrigin: { vertical: "bottom", horizontal: "center" },
                        autoHideDuration: 3000,
                    });
                },
            }
        );
    };

    const handleFailInspection = () => {
        mutateFailInspection(
            {
                inspectionLineId: inspectionLine.id,
                InspectorId: user.id,
            },
            {
                onSuccess: () => {
                    enqueueSnackbar("Inspection marked as failed successfully", {
                        variant: "success",
                        anchorOrigin: { vertical: "bottom", horizontal: "center" },
                        autoHideDuration: 3000,
                    });
                    setInspectionPassed(false);
                    refetchInspectionLine();
                },
                onError: () => {
                    enqueueSnackbar("Error occurred while failing the inspection", {
                        variant: "error",
                        anchorOrigin: { vertical: "bottom", horizontal: "center" },
                        autoHideDuration: 3000,
                    });
                },
            }
        );
    };

    const handleLineClick = useCallback(() => {
        if (!projectLine?.id) return;

        const serviceSection = projectLine?.project?.serviceType?.section?.toLowerCase();
        const isValidSection =
            serviceSection &&
            Object.values(SERVICE_SECTIONS)
                .map((type) => type.toLowerCase())
                .includes(serviceSection);

        let route = `/gardening/${projectLine.id}`; // Default route
        if (isValidSection) {
            switch (serviceSection) {
                case SERVICE_TYPES.WINTER_WORK.toLowerCase():
                    route = `/winterwork/${projectLine.id}`;
                    break;
                case SERVICE_TYPES.MOWING.toLowerCase():
                    route = `/mowing/${projectLine.id}`;
                    break;
                default:
                    break;
            }
        }

        history.push(route, {
            selectedList: [],
            selectedPostalCode: [],
            isAdminChecked: isAdmin,
            from: location?.pathname,
        });
    }, [projectLine, isAdmin, location?.pathname, history]);

    // render
    if (isErrorInspectionLine || isErrorProjectLine) {
        return <ErrorPage />;
    }
    if (isLoadingInspectionLine || isLoadingProjectLine) {
        return (
            <Layout title={`Inspection`} subtitle={user.name}>
                <Box display="flex" justifyContent="center">
                    <CircularProgress />
                </Box>
            </Layout>
        );
    }

    const isInspected = !!inspectionLine?.inspectedBy;
    const { project } = projectLine ?? {};
    const { address } = project ?? {};
    const { latitude, longitude } = address ?? {};

    return (
        <Layout title={`Inspection`} subtitle={user.name} map={{ latitude, longitude }}>
            <Grid container direction="column" spacing={4}>
                <Grid item container direction="column" spacing={1}>
                    <Grid item>
                        <TextField label="Comment" variant="filled" multiline fullWidth minRows={4} value={inspectionComment} onChange={handleChangeComment} />
                    </Grid>
                    <Grid item container spacing={1}>
                        <Grid item>
                            <LoadingButton
                                onClick={handleUpdateInspectionLineComment}
                                loading={isLoadingUpdateComment}
                                variant="outlined"
                                color="primary"
                                style={{ borderRadius: 50 }}
                                disabled={!isCommentChanged}
                            >
                                {`Save`}
                            </LoadingButton>
                        </Grid>
                        <Grid item>
                            <Button
                                onClick={handleCancelInspectionLineComment}
                                variant="outlined"
                                color="secondary"
                                style={{ borderRadius: 50 }}
                                disabled={!isCommentChanged || isLoadingUpdateComment}
                            >
                                {`Cancel`}
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item container direction="column" spacing={1}>
                    <Grid item>
                        <Typography variant="h6">{`Workline`}</Typography>
                    </Grid>
                    <Grid item>
                        {inspectionLine?.projectLine?.id ? (
                            projectLine && <WorklineListItem line={inspectionLine} projectLine={projectLine} onClick={handleLineClick} />
                        ) : (
                            <Typography>{`No linked workline found`}</Typography>
                        )}
                    </Grid>
                </Grid>

                <Grid item>
                    <Box display="flex" flexDirection="column">
                        <Typography variant="h6">{!isInspected ? "Awaiting Your Inspection" : "Inspection"}</Typography>
                        <Chip
                            avatar={
                                <Avatar>
                                    <PersonIcon />
                                </Avatar>
                            }
                            label={
                                <Box display="flex" flexWrap="wrap" maxWidth="100%" py={0.6}>
                                    <Typography variant="body2" style={{ paddingRight: "4px" }}>
                                        Inspected by:
                                    </Typography>
                                    <Typography variant="body2">{`${inspectionLine?.inspectedBy?.name || "Not yet inspected"}`}</Typography>
                                </Box>
                            }
                            variant="outlined"
                            style={{ marginTop: "8px", height: "auto" }}
                        />
                    </Box>
                </Grid>

                <Grid item container direction="column" spacing={1}>
                    <Grid item>
                        <LoadingButton
                            onClick={handlePassInspection}
                            loading={isPassInspectionLoading}
                            variant="contained"
                            color="primary"
                            fullWidth
                            startIcon={inspectionPassed ? <CheckIcon /> : null}
                        >
                            {inspectionPassed && isInspected ? "Passed" : "Mark as Passed"}
                        </LoadingButton>
                    </Grid>

                    <Grid item>
                        <LoadingButton
                            onClick={handleFailInspection}
                            loading={isFailInspectionLoading}
                            variant="contained"
                            color="secondary"
                            fullWidth
                            startIcon={!inspectionPassed && isInspected ? <CloseIcon /> : null}
                        >
                            {!inspectionPassed && isInspected ? "Failed" : "Mark as Failed"}
                        </LoadingButton>
                    </Grid>
                </Grid>
            </Grid>
        </Layout>
    );
}

export default InspectionDetails;
